import { mergeProps } from "@base-ui/react/merge-props";
import { useRender } from "@base-ui/react/use-render";

import { cn } from "../../lib/utils";

type BadgeVariantProps = {
  variant?:
    | "default"
    | "secondary"
    | "destructive"
    | "outline"
    | "tonal"
    | "purple"
    | "link";
  size?: "xs" | "sm" | "default" | "lg";
  radius?: "none" | "sm" | "default" | "md" | "lg" | "full";
};

const badgeVariants = {
  default: "bg-primary text-primary-foreground",
  secondary: "bg-secondary text-secondary-foreground",
  destructive:
    "bg-[color-mix(in_srgb,var(--destructive)_10%,transparent)] text-destructive",
  outline: "border-border text-foreground",
  tonal:
    "h-auto truncate rounded bg-tint-secondary px-1.5 text-xs text-text-secondary first-letter:capitalize [&_svg]:size-3.5",
  purple:
    "bg-bg-purple text-text-purple hover:bg-tint-purple hover:text-text-purple",
  link: "bg-transparent text-primary underline-offset-4 hover:underline",
} satisfies Record<NonNullable<BadgeVariantProps["variant"]>, string>;

const badgeSizes = {
  xs: "h-4 gap-0.5 px-1 text-caption leading-caption [&>svg]:size-3",
  sm: "h-5 gap-1 px-1.5 text-xs leading-xs [&>svg]:size-3",
  default: null,
  lg: "h-6 gap-1.5 px-2.5 text-nav leading-nav [&>svg]:size-3.5",
} satisfies Record<NonNullable<BadgeVariantProps["size"]>, string | null>;

const badgeRadii = {
  none: "rounded-none",
  sm: "rounded-sm",
  default: null,
  md: "rounded-md",
  lg: "rounded-lg",
  full: "rounded-full",
} satisfies Record<NonNullable<BadgeVariantProps["radius"]>, string | null>;

function Badge({
  className,
  variant = "default",
  size = "default",
  radius = "default",
  render,
  ...props
}: useRender.ComponentProps<"span"> & BadgeVariantProps) {
  const mergedClassName = cn(
    "inline-flex h-5 w-fit shrink-0 items-center justify-center gap-1 overflow-hidden whitespace-nowrap rounded-md border border-transparent px-2 py-0.5 text-xs font-medium leading-xs outline-none focus-visible:ring-1 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background [&>svg]:size-3",
    badgeVariants[variant],
    badgeSizes[size],
    badgeRadii[radius],
    className,
  );

  return useRender({
    defaultTagName: "span",
    props: mergeProps<"span">(
      {
        className: mergedClassName,
      },
      props,
    ),
    render,
    state: {
      slot: "badge",
      variant,
      size,
      radius,
    },
  });
}

function StatusDot({
  className,
  tone = "primary",
  ...props
}: React.ComponentProps<"span"> & {
  tone?: "primary" | "destructive" | "warning" | "neutral";
}) {
  return (
    <span
      data-slot="status-dot"
      className={cn(
        "inline-block size-2 shrink-0 rounded-full outline-none focus-visible:ring-1 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background",
        tone === "primary" && "bg-primary",
        tone === "destructive" && "bg-destructive",
        tone === "warning" && "bg-warning",
        tone === "neutral" && "bg-text-secondary",
        className,
      )}
      {...props}
    />
  );
}

export { Badge, StatusDot };
